// Page publique de contact (vitrine) : formulaire envoyé au support AERH.
// Poste sur /api/support/contact-public (sans authentification).
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";

const LOGO = "/logo_GAP.jpg";
const SUPPORT_EMAIL = "contact@groupeaerhpolynesie.com";
const SUPPORT_TEL = "87 73 78 15";

export default function Contact() {
  const [contact, setContact] = useState({ nom: "", email: "", sujet: "", message: "" });
  const [envoi, setEnvoi] = useState(false);

  const envoyer = async () => {
    if (!contact.nom || !contact.email || !contact.message) {
      return toast.error("Merci de renseigner votre nom, email et message.");
    }
    setEnvoi(true);
    try {
      const { data } = await api.post("/support/contact-public", contact);
      toast.success(data.message);
      setContact({ nom: "", email: "", sujet: "", message: "" });
    } catch {
      toast.error("L'envoi a échoué. Veuillez réessayer.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div data-testid="page-contact" className="min-h-screen bg-white text-[#1D3D3D] flex flex-col">
      <header className="border-b border-[#1D3D3D]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="Groupe AERH Polynésie — Porinetia Compta AERH" className="h-8 w-auto rounded" />
            <span className="text-base font-extrabold tracking-tight">Porinetia Compta AERH</span>
          </Link>
          <Button asChild variant="outline" className="border-[#1D3D3D]/20 text-[#1D3D3D]">
            <Link to="/" data-testid="retour-accueil-link">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour à l'accueil
            </Link>
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Contactez-nous</h1>
          <p className="mt-4 text-base text-[#1D3D3D]/70">
            Une question sur Porinetia Compta AERH, les formules ou votre comptabilité ? Notre équipe à Papeete
            vous répond en français, sous 48h ouvrées.
          </p>
          <ul className="mt-8 space-y-4 text-base">
            <li className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-[#4A8A8A] shrink-0 mt-0.5" />
              <span>10 Rue Georges LAGARDE — Papeete</span>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-[#4A8A8A] shrink-0" />
              <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-[#4A8A8A] transition-colors break-all">{SUPPORT_EMAIL}</a>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-[#4A8A8A] shrink-0" />
              <a href="tel:+68987737815" className="hover:text-[#4A8A8A] transition-colors">{SUPPORT_TEL}</a>
            </li>
          </ul>
        </div>

        <div className="bg-[#1D3D3D] text-white rounded-2xl p-6 shadow-lg">
          <h2 className="text-lg font-bold">Envoyez-nous un message</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-white/80">Nom</Label>
              <Input data-testid="contact-nom" value={contact.nom}
                onChange={(e) => setContact((c) => ({ ...c, nom: e.target.value }))} className="bg-white text-[#1D3D3D] h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-white/80">Email</Label>
              <Input data-testid="contact-email" type="email" value={contact.email}
                onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} className="bg-white text-[#1D3D3D] h-9" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold text-white/80">Sujet</Label>
              <Input data-testid="contact-sujet" value={contact.sujet}
                onChange={(e) => setContact((c) => ({ ...c, sujet: e.target.value }))} className="bg-white text-[#1D3D3D] h-9" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold text-white/80">Message</Label>
              <Textarea data-testid="contact-message" value={contact.message}
                onChange={(e) => setContact((c) => ({ ...c, message: e.target.value }))} className="bg-white text-[#1D3D3D]" rows={5} />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button data-testid="envoyer-message" onClick={envoyer} disabled={envoi}
              className="bg-white text-[#1D3D3D] hover:bg-white/90 font-semibold">
              {envoi ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Envoyer
            </Button>
          </div>
        </div>
      </main>

      <footer className="border-t border-[#1D3D3D]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-[#1D3D3D]/60">
          © 2026 Groupe AERH Polynésie — Tous droits réservés
        </div>
      </footer>
    </div>
  );
}
